import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
import { API_CONFIG } from "@/config/api.config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";

const BRAND = { navy: "#1D314D", gold: "#CEAB65" };

const LOAN_PRODUCT_TYPES = [
  "Senior Debt (LBO)", "Unitranche", "Commercial Loan", "SBA Loan",
  "Asset-Based Lending (ABL)", "Seller Financing (Subordinated Debt)",
  "Mezzanine", "Equipment Lease", "Real Estate Loan", "Bridge Loan", "Other Loan Type",
];
const LENDER_TYPES = [
  "SBA Preferred Lender", "Traditional Bank", "Non-Bank Lender", "FinTech Lender",
  "Private Lender", "Private Equity (PE)", "Family Office", "Equipment Financing",
  "Asset-Based Lender (ABL)", "Other Lender Type",
];
const COLLATERAL_OPTIONS = [
  "All Business Assets (First Lien)", "Accounts Receivable", "Inventory",
  "Equipment & Machinery", "Personal Real Estate", "Personal Guarantee (Secured)",
  "Personal Guarantee (Unsecured)", "Business Real Estate (First Lien)",
  "Intellectual Property", "Cash on Deposit",
];

const STEPS = [
  "Deal Summary", "Lender Info", "Loan Terms", "Fees",
  "Covenants & Collateral", "ABL & Subordinated", "Credit & International", "Review",
];

interface Fee {
  description: string;
  amount: number;
  type: "fixed" | "percentage_of_loan";
}

interface LenderOfferState {
  // Section 2
  lenderCompanyName: string;
  lenderLicenseOrNMLS: string;
  lenderRepresentativeName: string;
  lenderRepresentativePhone: string;
  lenderRepresentativeEmail: string;
  // Section 3
  lenderType: string;
  loanProductType: string;
  loanAmountOffered: number;
  interestRateType: "fixed" | "variable" | "blended";
  interestRateValue: number;
  interestRateMarginBps: number | null;
  interestRateFloor: number | null;
  loanTermLengthMonths: number;
  amortizationType: "linear" | "annuity" | "bullet" | "pik";
  prepaymentPenalty: string;
  // Section 4
  arrangementFeePercent: number | null;
  drawFeePercentABL: number | null;
  exitFeePercent: number | null;
  feesList: Fee[];
  // Section 5
  maintenanceCovenantsRequired: boolean;
  netDebtToEBITDAMaxAllowed: number | null;
  dscrMinimumRequired: number;
  dscrCalculationBase: "cfads" | "sde" | "ebitda";
  dividendRestrictions: string;
  collateralRequired: boolean;
  collateralRequirements: string[];
  personalGuaranteeRequired: boolean;
  realEstateConditioned: boolean;
  // Section 6
  isABLOffer: boolean;
  ABL_AR_EligiblityPercent: number | null;
  ABL_Inventory_EligiblityPercent: number | null;
  ABL_Cash_Sweep: boolean;
  isSubordinatedDebt: boolean;
  subordinationToSeniorDebt: boolean;
  interestGracePeriodMonths: number | null;
  balloonPaymentYear: number | null;
  balloonPaymentAmount: number | null;
  // Section 7
  creditScoreRequired: boolean;
  creditScoreMinimum: number | null;
  acceptForeignCreditScores: boolean;
  foreignCreditScoreMinimum: number | null;
  acceptedForeignCreditCountries: string[];
  acceptForeignCollateral: boolean;
  foreignCollateralDetails: string;
  // Section 8
  estimatedClosingTimelineDays: number;
  offerExpirationDate: string;
}

const INITIAL_STATE: LenderOfferState = {
  lenderCompanyName: "", lenderLicenseOrNMLS: "", lenderRepresentativeName: "",
  lenderRepresentativePhone: "", lenderRepresentativeEmail: "",
  lenderType: "", loanProductType: "", loanAmountOffered: 0,
  interestRateType: "fixed", interestRateValue: 0, interestRateMarginBps: null,
  interestRateFloor: null, loanTermLengthMonths: 0, amortizationType: "linear",
  prepaymentPenalty: "",
  arrangementFeePercent: null, drawFeePercentABL: null, exitFeePercent: null,
  feesList: [],
  maintenanceCovenantsRequired: false, netDebtToEBITDAMaxAllowed: null,
  dscrMinimumRequired: 1.3, dscrCalculationBase: "cfads", dividendRestrictions: "",
  collateralRequired: false, collateralRequirements: [],
  personalGuaranteeRequired: false, realEstateConditioned: false,
  isABLOffer: false, ABL_AR_EligiblityPercent: null, ABL_Inventory_EligiblityPercent: null,
  ABL_Cash_Sweep: false, isSubordinatedDebt: false, subordinationToSeniorDebt: false,
  interestGracePeriodMonths: null, balloonPaymentYear: null, balloonPaymentAmount: null,
  creditScoreRequired: false, creditScoreMinimum: null,
  acceptForeignCreditScores: false, foreignCreditScoreMinimum: null,
  acceptedForeignCreditCountries: [], acceptForeignCollateral: false,
  foreignCollateralDetails: "",
  estimatedClosingTimelineDays: 30, offerExpirationDate: "",
};

interface Props {
  dealId?: string;
}

export default function LenderOfferFormV3({ dealId: propDealId }: Props) {
  const { toast } = useToast();
  const [dealId] = useState(propDealId || new URLSearchParams(window.location.search).get("dealId") || "");
  const [step, setStep] = useState(1);
  const [data, setData] = useState<LenderOfferState>(INITIAL_STATE);
  const [deal, setDeal] = useState<any>(null);
  const [loadingDeal, setLoadingDeal] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [restorePrompt, setRestorePrompt] = useState<LenderOfferState | null>(null);

  const lenderId = auth.currentUser?.uid;
  const draftKey = useMemo(
    () => (lenderId && dealId ? `${lenderId}_${dealId}` : null),
    [lenderId, dealId],
  );

  // Load deal
  useEffect(() => {
    if (!dealId) { setLoadingDeal(false); return; }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "dealSubmissions", dealId));
        if (snap.exists()) setDeal({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error("Failed to load deal:", e);
      } finally {
        setLoadingDeal(false);
      }
    })();
  }, [dealId]);

  // Load draft
  useEffect(() => {
    if (!draftKey) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "lenderOfferDrafts", draftKey));
        if (snap.exists()) setRestorePrompt(snap.data() as LenderOfferState);
      } catch (e) { console.error("draft load", e); }
    })();
  }, [draftKey]);

  // Auto-save every 60s
  useEffect(() => {
    if (!draftKey) return;
    const id = setInterval(async () => {
      try {
        await setDoc(doc(db, "lenderOfferDrafts", draftKey), {
          ...data, updatedAt: new Date().toISOString(),
        });
      } catch (e) { console.error("auto-save", e); }
    }, 60_000);
    return () => clearInterval(id);
  }, [data, draftKey]);

  const update = <K extends keyof LenderOfferState>(k: K, v: LenderOfferState[K]) =>
    setData((d) => ({ ...d, [k]: v }));

  const validateStep = (s: number): boolean => {
    if (s === 2) {
      if (!data.lenderCompanyName || !data.lenderRepresentativeName ||
          !data.lenderRepresentativeEmail || !data.lenderRepresentativePhone) {
        toast({ variant: "destructive", title: "Required fields missing" });
        return false;
      }
    }
    if (s === 3) {
      if (!data.lenderType || !data.loanProductType ||
          !data.loanAmountOffered || !data.interestRateValue) {
        toast({ variant: "destructive", title: "Required fields missing" });
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep(step)) setStep((s) => Math.min(8, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!dealId) {
      toast({ variant: "destructive", title: "Missing dealId" });
      return;
    }
    setSubmitting(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_CONFIG.baseURL}/lender-offers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ dealId, ...data, submittedAt: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: "Offer submitted successfully" });
      if (draftKey) {
        try { await deleteDoc(doc(db, "lenderOfferDrafts", draftKey)); } catch {}
      }
      setData(INITIAL_STATE);
      setStep(1);
    } catch (e: any) {
      console.error(e);
      toast({ variant: "destructive", title: "Submission failed", description: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const addFee = () =>
    update("feesList", [...data.feesList, { description: "", amount: 0, type: "fixed" }]);
  const updateFee = (i: number, patch: Partial<Fee>) =>
    update("feesList", data.feesList.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeFee = (i: number) =>
    update("feesList", data.feesList.filter((_, idx) => idx !== i));

  const toggleCollateral = (opt: string) => {
    const has = data.collateralRequirements.includes(opt);
    update(
      "collateralRequirements",
      has ? data.collateralRequirements.filter((x) => x !== opt) : [...data.collateralRequirements, opt],
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Dialog open={!!restorePrompt} onOpenChange={() => setRestorePrompt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore draft?</DialogTitle>
            <DialogDescription>
              You have an unsubmitted draft for this deal. Restore it?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestorePrompt(null)}>Start fresh</Button>
            <Button
              style={{ backgroundColor: BRAND.navy }}
              onClick={() => { if (restorePrompt) setData(restorePrompt); setRestorePrompt(null); }}
            >Restore</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between mb-2 text-xs">
          {STEPS.map((s, i) => (
            <span
              key={s}
              className="flex-1 text-center"
              style={{ color: i + 1 === step ? BRAND.navy : "#888", fontWeight: i + 1 === step ? 700 : 400 }}
            >{i + 1}. {s}</span>
          ))}
        </div>
        <Progress value={(step / 8) * 100} className="[&>div]:bg-[#CEAB65]" />
      </div>

      <Card>
        <CardHeader style={{ backgroundColor: BRAND.navy }} className="text-white rounded-t-lg">
          <CardTitle>Step {step}: {STEPS[step - 1]}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">

          {step === 1 && (
            loadingDeal ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
            ) : !deal ? (
              <p className="text-muted-foreground">No deal found{dealId ? ` for ID: ${dealId}` : ""}.</p>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info label="Deal Name" value={deal.dealName || deal.businessName} />
                <Info label="Industry" value={deal.industry || deal.dealIndustry} />
                <Info label="Location" value={deal.location || deal.dealLocation} />
                <Info label="Asking Price" value={fmt(deal.askingPrice || deal.targetAskPrice)} />
                <Info label="SDE (avg)" value={fmt(deal.SDE_avg)} />
                <Info label="DSCR (system)" value={deal.DSCR_calculated_system} />
                <Info label="LTV (system)" value={deal.LTV_calculated_system} />
                <Info label="Customer Concentration" value={deal.topCustomerConcentrationPercent ? `${deal.topCustomerConcentrationPercent}%` : "—"} />
                <Info label="Mgmt Team Score" value={deal.managementTeamStrengthScore} />
                <div className="col-span-2">
                  <Label className="font-semibold">Adjusted EBITDA (L3Y)</Label>
                  <pre className="text-xs bg-muted p-2 rounded mt-1">
                    {JSON.stringify(deal.adjustedEBITDA_L3Y || [], null, 2)}
                  </pre>
                </div>
                <div className="col-span-2">
                  <Label className="font-semibold">Biggest Weakness</Label>
                  <p className="text-muted-foreground">{deal.dealBiggestWeakness || "—"}</p>
                </div>
                <div className="col-span-2">
                  <Label className="font-semibold">Existing Debt to Refinance</Label>
                  <pre className="text-xs bg-muted p-2 rounded mt-1">
                    {JSON.stringify(deal.existingDebtToRefinance || [], null, 2)}
                  </pre>
                </div>
              </div>
            )
          )}

          {step === 2 && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Company Name *" value={data.lenderCompanyName} onChange={(v) => update("lenderCompanyName", v)} />
              <Field label="License / NMLS" value={data.lenderLicenseOrNMLS} onChange={(v) => update("lenderLicenseOrNMLS", v)} />
              <Field label="Representative Name *" value={data.lenderRepresentativeName} onChange={(v) => update("lenderRepresentativeName", v)} />
              <Field label="Phone *" value={data.lenderRepresentativePhone} onChange={(v) => update("lenderRepresentativePhone", v)} />
              <Field label="Email *" type="email" value={data.lenderRepresentativeEmail} onChange={(v) => update("lenderRepresentativeEmail", v)} />
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="Lender Type *" value={data.lenderType} options={LENDER_TYPES} onChange={(v) => update("lenderType", v)} />
              <SelectField label="Loan Product Type *" value={data.loanProductType} options={LOAN_PRODUCT_TYPES} onChange={(v) => update("loanProductType", v)} />
              <NumField label="Loan Amount Offered ($) *" value={data.loanAmountOffered} onChange={(v) => update("loanAmountOffered", v)} />
              <SelectField label="Interest Rate Type" value={data.interestRateType} options={["fixed", "variable", "blended"]} onChange={(v) => update("interestRateType", v as any)} />
              <NumField label="Interest Rate (%) *" value={data.interestRateValue} step="0.01" onChange={(v) => update("interestRateValue", v)} />
              <NumField label="Margin (bps)" value={data.interestRateMarginBps ?? ""} onChange={(v) => update("interestRateMarginBps", v || null)} />
              <NumField label="Rate Floor (%)" value={data.interestRateFloor ?? ""} step="0.01" onChange={(v) => update("interestRateFloor", v || null)} />
              <NumField label="Term (months)" value={data.loanTermLengthMonths} onChange={(v) => update("loanTermLengthMonths", v)} />
              <SelectField label="Amortization" value={data.amortizationType} options={["linear", "annuity", "bullet", "pik"]} onChange={(v) => update("amortizationType", v as any)} />
              <Field label="Prepayment Penalty" value={data.prepaymentPenalty} onChange={(v) => update("prepaymentPenalty", v)} />
            </div>
          )}

          {step === 4 && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <NumField label="Arrangement Fee (%)" value={data.arrangementFeePercent ?? ""} step="0.01" onChange={(v) => update("arrangementFeePercent", v || null)} />
                <NumField label="Draw Fee (% ABL)" value={data.drawFeePercentABL ?? ""} step="0.01" onChange={(v) => update("drawFeePercentABL", v || null)} />
                <NumField label="Exit Fee (%)" value={data.exitFeePercent ?? ""} step="0.01" onChange={(v) => update("exitFeePercent", v || null)} />
              </div>
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <Label className="font-semibold">Additional Fees</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addFee}><Plus className="w-4 h-4 mr-1" />Add</Button>
                </div>
                {data.feesList.map((f, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                    <Input className="col-span-5" placeholder="Description" value={f.description} onChange={(e) => updateFee(i, { description: e.target.value })} />
                    <Input className="col-span-3" type="number" placeholder="Amount" value={f.amount} onChange={(e) => updateFee(i, { amount: Number(e.target.value) })} />
                    <Select value={f.type} onValueChange={(v) => updateFee(i, { type: v as any })}>
                      <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="percentage_of_loan">% of Loan</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeFee(i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <CheckRow label="Maintenance Covenants Required" checked={data.maintenanceCovenantsRequired} onChange={(v) => update("maintenanceCovenantsRequired", v)} />
              <div className="grid grid-cols-2 gap-4">
                <NumField label="Net Debt / EBITDA Max" value={data.netDebtToEBITDAMaxAllowed ?? ""} step="0.01" onChange={(v) => update("netDebtToEBITDAMaxAllowed", v || null)} />
                <NumField label="DSCR Minimum" value={data.dscrMinimumRequired} step="0.01" onChange={(v) => update("dscrMinimumRequired", v)} />
                <SelectField label="DSCR Calculation Base" value={data.dscrCalculationBase} options={["cfads", "sde", "ebitda"]} onChange={(v) => update("dscrCalculationBase", v as any)} />
              </div>
              <div>
                <Label>Dividend Restrictions</Label>
                <Textarea value={data.dividendRestrictions} onChange={(e) => update("dividendRestrictions", e.target.value)} />
              </div>
              <CheckRow label="Collateral Required" checked={data.collateralRequired} onChange={(v) => update("collateralRequired", v)} />
              {data.collateralRequired && (
                <div className="grid grid-cols-2 gap-2 pl-4">
                  {COLLATERAL_OPTIONS.map((opt) => (
                    <CheckRow key={opt} label={opt} checked={data.collateralRequirements.includes(opt)} onChange={() => toggleCollateral(opt)} />
                  ))}
                </div>
              )}
              <CheckRow label="Personal Guarantee Required" checked={data.personalGuaranteeRequired} onChange={(v) => update("personalGuaranteeRequired", v)} />
              <CheckRow label="Real Estate Conditioned" checked={data.realEstateConditioned} onChange={(v) => update("realEstateConditioned", v)} />
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4">
              <CheckRow label="This is an ABL Offer" checked={data.isABLOffer} onChange={(v) => update("isABLOffer", v)} />
              {data.isABLOffer && (
                <div className="grid grid-cols-2 gap-4 pl-4">
                  <NumField label="AR Eligibility (%)" value={data.ABL_AR_EligiblityPercent ?? ""} step="0.01" onChange={(v) => update("ABL_AR_EligiblityPercent", v || null)} />
                  <NumField label="Inventory Eligibility (%)" value={data.ABL_Inventory_EligiblityPercent ?? ""} step="0.01" onChange={(v) => update("ABL_Inventory_EligiblityPercent", v || null)} />
                  <CheckRow label="Cash Sweep" checked={data.ABL_Cash_Sweep} onChange={(v) => update("ABL_Cash_Sweep", v)} />
                </div>
              )}
              <CheckRow label="Subordinated Debt" checked={data.isSubordinatedDebt} onChange={(v) => update("isSubordinatedDebt", v)} />
              {data.isSubordinatedDebt && (
                <div className="grid grid-cols-2 gap-4 pl-4">
                  <CheckRow label="Subordination to Senior Debt" checked={data.subordinationToSeniorDebt} onChange={(v) => update("subordinationToSeniorDebt", v)} />
                  <NumField label="Interest Grace Period (months)" value={data.interestGracePeriodMonths ?? ""} onChange={(v) => update("interestGracePeriodMonths", v || null)} />
                  <NumField label="Balloon Payment Year" value={data.balloonPaymentYear ?? ""} onChange={(v) => update("balloonPaymentYear", v || null)} />
                  <NumField label="Balloon Payment Amount" value={data.balloonPaymentAmount ?? ""} onChange={(v) => update("balloonPaymentAmount", v || null)} />
                </div>
              )}
            </div>
          )}

          {step === 7 && (
            <div className="space-y-4">
              <CheckRow label="Credit Score Required" checked={data.creditScoreRequired} onChange={(v) => update("creditScoreRequired", v)} />
              {data.creditScoreRequired && (
                <NumField label="Minimum Credit Score" value={data.creditScoreMinimum ?? ""} onChange={(v) => update("creditScoreMinimum", v || null)} />
              )}
              <CheckRow label="Accept Foreign Credit Scores" checked={data.acceptForeignCreditScores} onChange={(v) => update("acceptForeignCreditScores", v)} />
              {data.acceptForeignCreditScores && (
                <>
                  <NumField label="Foreign Credit Score Min" value={data.foreignCreditScoreMinimum ?? ""} onChange={(v) => update("foreignCreditScoreMinimum", v || null)} />
                  <div>
                    <Label>Accepted Countries (comma-separated)</Label>
                    <Input
                      value={data.acceptedForeignCreditCountries.join(", ")}
                      onChange={(e) => update("acceptedForeignCreditCountries", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                    />
                  </div>
                </>
              )}
              <CheckRow label="Accept Foreign Collateral" checked={data.acceptForeignCollateral} onChange={(v) => update("acceptForeignCollateral", v)} />
              {data.acceptForeignCollateral && (
                <div>
                  <Label>Foreign Collateral Details</Label>
                  <Textarea value={data.foreignCollateralDetails} onChange={(e) => update("foreignCollateralDetails", e.target.value)} />
                </div>
              )}
            </div>
          )}

          {step === 8 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <NumField label="Estimated Closing Timeline (days)" value={data.estimatedClosingTimelineDays} onChange={(v) => update("estimatedClosingTimelineDays", v)} />
                <div>
                  <Label>Offer Expiration Date</Label>
                  <Input type="date" value={data.offerExpirationDate} onChange={(e) => update("offerExpirationDate", e.target.value)} />
                </div>
              </div>
              <Card>
                <CardHeader><CardTitle className="text-base">Review Summary</CardTitle></CardHeader>
                <CardContent>
                  <pre className="text-xs bg-muted p-3 rounded max-h-80 overflow-auto">
                    {JSON.stringify({ dealId, ...data }, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Nav */}
          <div className="flex justify-between pt-6 border-t">
            <Button variant="outline" onClick={back} disabled={step === 1}>Back</Button>
            {step < 8 ? (
              <Button style={{ backgroundColor: BRAND.navy }} onClick={next}>Next</Button>
            ) : (
              <Button style={{ backgroundColor: BRAND.gold, color: BRAND.navy }} onClick={submit} disabled={submitting}>
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : "Submit Offer"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Info({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <Label className="font-semibold">{label}</Label>
      <p className="text-muted-foreground">{value ?? "—"}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function NumField({ label, value, onChange, step = "1" }: { label: string; value: number | string; onChange: (v: number) => void; step?: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type="number" step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function CheckRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <Label className="cursor-pointer" onClick={() => onChange(!checked)}>{label}</Label>
    </div>
  );
}

function fmt(v: any): string {
  if (v == null || v === "") return "—";
  if (typeof v === "number") return `$${v.toLocaleString("en-US")}`;
  return String(v);
}
